/**
 * Talking with Connect (2026-10-21): the browser's side of a Gemini Live conversation.
 *
 * The session (a one-use token and the setup: Connect's instructions and tools) comes from Connect's API; the key
 * never reaches the browser. The microphone goes to the model as 16 kHz PCM, the model's voice comes back as 24 kHz
 * PCM and plays as it arrives; talking over it stops it (the model hears you and says interrupted). Tool calls go to
 * Connect's API (onTool, run there as the person) and their answers back to the model. What both sides say shows as
 * captions (onCaption) and is kept in the chat when a turn is over (onSave): the person's words when the model starts
 * on them (so a chart a tool puts in the chat comes after the question), Connect's words when it has finished.
 */

// Runs in the audio thread: the microphone at the device's rate, out as 16 kHz 16-bit frames of 40 ms.
const CAPTURE = `
class ConnectCapture extends AudioWorkletProcessor {
  constructor(o) { super(); this.step = sampleRate / o.processorOptions.rate; this.pos = 0; this.out = new Int16Array(640); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (; this.pos < ch.length; this.pos += this.step) {
      const i = Math.floor(this.pos), f = this.pos - i;
      const s = i + 1 < ch.length ? ch[i] + (ch[i + 1] - ch[i]) * f : ch[i];
      this.out[this.n++] = Math.max(-1, Math.min(1, s)) * 0x7fff;
      if (this.n === this.out.length) { this.port.postMessage(this.out.slice(0)); this.n = 0; }
    }
    this.pos -= ch.length;
    return true;
  }
}
registerProcessor('connect-capture', ConnectCapture);`;

const IN_RATE = 16000;
const OUT_RATE = 24000;

/** Whether this browser can talk with Connect: a microphone and the audio thread it needs. */
export function voiceSupported() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.AudioWorkletNode && window.WebSocket);
}

function toBase64(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function fromBase64(b64) {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes;
}

/** Loudness of 16-bit samples, 0..1, for the level ring (a square root so quiet speech still shows). */
function level(samples) {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.min(1, Math.sqrt(Math.sqrt(sum / samples.length) / 0x7fff) * 1.6);
}

export class LiveVoice {
  /**
   * on: { state(s), caption({ user, model }), tool(name, args, heard) -> Promise<result>, save({ user, model }) -> Promise,
   *       level(v), error(message), end(reason) }. States: connecting, listening, working, speaking, ended.
   */
  constructor(on) {
    this.on = on;
    this.ws = null; this.stream = null; this.ctxIn = null; this.ctxOut = null;
    this.sources = new Set(); this.playAt = 0; this.stopped = false;
    this.turn = { user: "", model: "", userSaved: false };   // what is kept in the chat (reset when a turn is over)
    this.shown = { user: "", model: "" };                     // the captions: the last question and its answer, until the next question
    this.levelAt = 0;
  }

  /** Call it from the click that asks for voice: the browser lets sound start only from a person's gesture. */
  prepare() {
    this.ctxOut = new AudioContext({ sampleRate: OUT_RATE });
    this.ctxIn = new AudioContext();
  }

  async start(session) {
    this.state("connecting");
    if (!this.ctxOut) this.prepare();
    this.ctxOut.resume().catch(() => {});
    this.ctxIn.resume().catch(() => {});
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
    if (this.stopped) return this.release();
    const url = URL.createObjectURL(new Blob([CAPTURE], { type: "application/javascript" }));
    try { await this.ctxIn.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
    if (this.stopped) return this.release();

    await new Promise((resolve, reject) => {
      this.ws = new WebSocket(session.ws_url + "?access_token=" + encodeURIComponent(session.token));
      this.ws.binaryType = "arraybuffer";   // read at once, in the order they came (a Blob is read later)
      this.ws.onopen = () => this.send({ setup: session.setup });
      this.ws.onerror = () => reject(new Error("Connect could not reach the voice service."));
      this.ws.onclose = (ev) => {
        if (this.ready) this.closed(ev);
        else reject(new Error(ev.reason || "The voice service closed the connection."));
      };
      this.ws.onmessage = (ev) => {
        const msg = JSON.parse(typeof ev.data === "string" ? ev.data : new TextDecoder().decode(ev.data));
        if (msg.setupComplete && !this.ready) { this.ready = true; resolve(); return; }
        this.message(msg);
      };
    });
    if (this.stopped) return this.release();

    // Only now does the microphone go out: the model is ready to hear it.
    const source = this.ctxIn.createMediaStreamSource(this.stream);
    this.capture = new AudioWorkletNode(this.ctxIn, "connect-capture", { processorOptions: { rate: IN_RATE } });
    this.capture.port.onmessage = (e) => this.mic(e.data);
    source.connect(this.capture);
    this.state("listening");
  }

  /** Stop: the microphone off, the voice silenced, the connection closed; what was said so far is kept. */
  async stop() {
    if (this.stopped) return;
    this.stopped = true;
    await this.keep(true);
    this.release();
    this.state("ended");
  }

  // ------------------------------------------------------------------ inside

  send(obj) { if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify(obj)); }

  state(s) { this.current = s; this.on.state(s); }

  mic(frame) {
    this.send({ realtimeInput: { audio: { data: toBase64(new Uint8Array(frame.buffer)), mimeType: "audio/pcm;rate=" + IN_RATE } } });
    if (this.current === "listening") this.levelNow(level(frame));
  }

  levelNow(v) {
    const t = performance.now();
    if (t - this.levelAt > 80) { this.levelAt = t; this.on.level(v); }
  }

  async message(msg) {
    if (msg.toolCall) return this.tools(msg.toolCall.functionCalls || []);
    if (msg.goAway) { this.endReason = "The voice session reached its time limit (about ten minutes). Tap the mic to go on."; return; }
    const sc = msg.serverContent;
    if (!sc) return;
    if (sc.inputTranscription && sc.inputTranscription.text) {
      if (this.shown.model && !this.turn.user) this.shown = { user: "", model: "" };   // a new question after an answer
      this.turn.user += sc.inputTranscription.text; this.shown.user += sc.inputTranscription.text; this.caption();
    }
    if (sc.outputTranscription && sc.outputTranscription.text) { this.turn.model += sc.outputTranscription.text; this.shown.model += sc.outputTranscription.text; this.caption(); }
    if (sc.interrupted) this.silence();
    for (const p of (sc.modelTurn && sc.modelTurn.parts) || []) {
      if (p.inlineData && p.inlineData.data) this.play(p.inlineData.data);
    }
    if (sc.turnComplete) this.keep(false);
  }

  caption() { this.on.caption({ user: this.shown.user.trim(), model: this.shown.model.trim() }); }

  /** The model's calls, run by Connect's API one after the other; the answers go back together. */
  async tools(calls) {
    this.state("working");
    const heard = this.turn.user.trim();   // taken now: the turn may be over (and emptied) before the calls are run
    await this.saveUser();
    const answers = [];
    for (const c of calls) {
      let response;
      try { response = await this.on.tool(c.name, c.args || {}, heard); }
      catch (e) { response = { ok: false, error: "Connect could not run that just now." }; }
      answers.push({ id: c.id, name: c.name, response });
    }
    this.send({ toolResponse: { functionResponses: answers } });
  }

  async saveUser() {
    if (this.turn.userSaved || !this.turn.user.trim()) return;
    this.turn.userSaved = true;
    try { await this.on.save({ user: this.turn.user.trim(), model: "" }); } catch (e) { /* kept in the captions only */ }
  }

  /** A finished turn (or the end): the words not kept yet go into the chat, and the next turn starts empty. */
  async keep(ending) {
    const t = this.turn;
    this.turn = { user: "", model: "", userSaved: false };
    const user = t.userSaved ? "" : t.user.trim();
    const model = t.model.trim() + (ending && t.model.trim() && this.sources.size ? "…" : "");
    if (user || model) { try { await this.on.save({ user, model }); } catch (e) { /* kept in the captions only */ } }
    if (!ending && !this.sources.size && this.current !== "working") this.state("listening");
  }

  /** 24 kHz PCM from the model, queued to play right after what is already playing. */
  play(b64) {
    if (!this.ctxOut) return;
    const pcm = new Int16Array(fromBase64(b64).buffer);
    const buf = this.ctxOut.createBuffer(1, pcm.length, OUT_RATE);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 0x8000;
    const src = this.ctxOut.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctxOut.destination);
    const at = Math.max(this.ctxOut.currentTime + 0.02, this.playAt);
    src.start(at);
    this.playAt = at + buf.duration;
    this.sources.add(src);
    this.state("speaking");
    this.levelNow(level(pcm));
    src.onended = () => {
      this.sources.delete(src);
      if (!this.sources.size && this.current === "speaking") this.state("listening");
    };
  }

  /** The person talked over the answer: it stops at once. */
  silence() {
    for (const s of this.sources) { try { s.stop(); } catch (e) { /* already ended */ } }
    this.sources.clear();
    this.playAt = 0;
    if (this.current === "speaking") this.state("listening");
  }

  closed(ev) {
    if (this.stopped) return;
    this.stopped = true;
    this.keep(true);
    this.release();
    this.on.end(this.endReason || (ev.code === 1000 ? "The voice session ended. Tap the mic to talk again." : "The voice session stopped (" + (ev.reason || ev.code) + "). Tap the mic to talk again."));
    this.state("ended");
  }

  release() {
    this.silence();
    if (this.capture) { this.capture.port.onmessage = null; try { this.capture.disconnect(); } catch (e) { /* gone */ } }
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    if (this.ws) { this.ws.onclose = null; this.ws.onmessage = null; try { this.ws.close(); } catch (e) { /* closed */ } }
    if (this.ctxIn) this.ctxIn.close().catch(() => {});
    if (this.ctxOut) this.ctxOut.close().catch(() => {});
    this.ws = null; this.stream = null; this.ctxIn = null; this.ctxOut = null; this.capture = null;
  }
}
